import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import {
  approveCosting,
  createCosting,
  discardCosting,
  getCosting,
  getCostingAudit,
  listMaterialRates,
  refreshCostingPrefill,
  rejectCosting,
  reviseCosting,
  saveCosting,
  submitCosting,
  withdrawCosting,
} from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import PageHeader, { HeaderButton } from "./ui/PageHeader";
import { askReason, confirmDialog } from "./ui/dialog";
import { useKT } from "./i18n/kt";
import { fmtDay, rupees } from "./rates/rateModel";
import {
  byDays,
  compute,
  FIELD_LABEL,
  GROUPS,
  Group,
  Header,
  Kind,
  KINDS,
  koriGrams,
  Layers,
  Line,
  METHODS,
  Method,
  newLine,
  planned,
  STATUS_LABEL,
  localDay,
} from "./costing/costingModel";
import CostingProductPanel from "./costing/CostingProductPanel";
import CostingPrint from "./costing/CostingPrint";
import CostingPdfPreview from "./costing/CostingPdfPreview";
import { rememberKarkhanaHere } from "./ui/leaveKarkhana";
import "./costing/karkhana-costing.scss";
import { kp } from "./karkhanaPaths";
import { useKarkhanaTitle } from "./ui/addressState";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

type Draft = { header: Header; lines: Line[]; layers: Layers; note?: string | null };

const lineId = (l: Line, i: number) => l.id || `new-${i}`;

/** A number box: empty means "not thought about yet", which blocks sending for approval. */
const NumIn = ({
  value,
  onChange,
  label,
  disabled,
  blank,
  suffix,
  width,
}: {
  value?: string | null;
  onChange: (v: string) => void;
  label: string;
  disabled?: boolean;
  blank?: boolean;
  suffix?: string;
  width?: number;
}) => (
  <label className={`kc-num${blank ? " is-blank" : ""}`} style={width ? { width } : undefined}>
    <span className="kc-num__label">{label}</span>
    <span className="kc-num__box">
      <input
        inputMode="decimal"
        value={value ?? ""}
        disabled={disabled}
        placeholder="—"
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
      />
      {suffix ? <span className="kc-num__suffix">{suffix}</span> : null}
    </span>
  </label>
);

/**
 * The costing sheet for one saree, laid out like the studio's Google Sheet: lines by group, what each
 * comes to, HKP's estimate beside it, then overhead, profit, discount and GST down to the sale price.
 * Owner/admins and managers only. Every number must be filled (0 is fine) before it can be approved.
 */
const KarkhanaCostingPage = () => {
  const { t, lang } = useKT();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const role = readRole();
  const [data, setData] = useState<any>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showBlanks, setShowBlanks] = useState(false);
  const [rates, setRates] = useState<any[]>([]);
  const [audit, setAudit] = useState<any[] | null>(null);
  const [adding, setAdding] = useState<{ group: Group; kind: Kind; label: string } | null>(null);
  const firstBlank = useRef<HTMLDivElement | null>(null);
  // Groups start folded to one line with their total; open one to see and change its lines.
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState(false);

  const take = useCallback((view: any) => {
    setData(view);
    const c = view?.costing;
    if (c) {
      setDraft({ header: { ...c.header }, lines: (c.lines || []).map((l: Line) => ({ ...l })), layers: { ...c.layers }, note: c.note });
      setDirty(false);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const res: any = await getCosting(id);
      if (res?.success) take(res.data);
      else notifications({ title: t("Error"), message: t(res?.message || "Could not load"), type: "danger" });
    } catch {
      notifications({ title: t("No connection"), message: t("This needs a connection. Try again in a moment."), type: "warning" });
    }
  }, [id, take]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    load();
    setAudit(null);
    listMaterialRates()
      .then((res: any) => setRates((res?.data || []).filter((r: any) => r.active !== false)))
      .catch(() => undefined);
  }, [role, history, load]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const calc = useMemo(() => (draft ? compute(draft.lines, draft.layers, draft.header) : null), [draft]);
  const blankSet = useMemo(() => {
    const s = new Set<string>();
    if (calc && showBlanks) [...calc.blanks, ...calc.bad].forEach((b) => s.add(`${b.lineId}:${b.field}`));
    if (calc) calc.bad.forEach((b) => s.add(`${b.lineId}:${b.field}`));
    return s;
  }, [calc, showBlanks]);

  useKarkhanaTitle(draft?.header?.sareeNumber ? `Costing ${draft.header.sareeNumber}` : draft?.header?.designNumber ? `Estimate ${draft.header.designNumber}` : null);
  if (!canManageKarkhana(role)) return null;

  const c = data?.costing;
  const canEdit = !!data?.canEdit;
  const est: Record<string, any> = data?.estimates?.lines || {};
  const warnings: any[] = data?.estimates?.warnings || [];
  const isBlank = (lid: string, field: string) => blankSet.has(`${lid}:${field}`);

  const change = (fn: (d: Draft) => Draft) => {
    setDraft((d) => (d ? fn(d) : d));
    setDirty(true);
  };
  const setLine = (i: number, patch: Partial<Line>) =>
    change((d) => ({ ...d, lines: d.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const setHeader = (patch: Partial<Header>) => change((d) => ({ ...d, header: { ...d.header, ...patch } }));
  const setLayer = (patch: Partial<Layers>) => change((d) => ({ ...d, layers: { ...d.layers, ...patch } }));

  const save = async (quiet = false): Promise<boolean> => {
    if (!draft) return false;
    if (calc && calc.bad.length) {
      notifications({ title: t("Check the numbers"), message: t("{{name}}: enter a number", { name: calc.bad[0].label }), type: "warning" });
      return false;
    }
    if (draft.lines.some((l) => !l.label.trim())) {
      notifications({ title: t("Not saved"), message: t("Every line needs a name"), type: "warning" });
      return false;
    }
    setBusy(true);
    try {
      const res: any = await saveCosting(id, draft as unknown as Record<string, unknown>);
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Could not save"), type: "danger" });
        return false;
      }
      take(res.data);
      setAudit(null);
      if (!quiet) notifications({ title: t("Saved"), message: t("Every change is kept in the history."), type: "success" });
      return true;
    } catch {
      notifications({ title: t("No connection"), message: t("Nothing was saved. Try again."), type: "warning" });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn: () => Promise<any>, done: string) => {
    setBusy(true);
    try {
      const res: any = await fn();
      if (!res?.success) {
        notifications({ title: t("Not done"), message: t(res?.message || "Could not do that"), type: "danger" });
        return null;
      }
      notifications({ title: t("Done"), message: t(done), type: "success" });
      setAudit(null);
      return res.data;
    } catch {
      notifications({ title: t("No connection"), message: t("Try again in a moment."), type: "warning" });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!calc) return;
    if (calc.blanks.length || calc.bad.length) {
      setShowBlanks(true);
      notifications({
        title: t("{{n}} numbers still empty", { n: calc.blanks.length + calc.bad.length }),
        message: t("Every line needs a number. Put 0 where it costs nothing, so we know it was thought about."),
        type: "warning",
      });
      window.setTimeout(() => firstBlank.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }
    if (dirty && !(await save(true))) return;
    const v = await act(() => submitCosting(id), "Sent to the owner for approval.");
    if (v) take(v);
  };

  const approve = async () => {
    const p = data?.product;
    const tt = calc?.totals;
    const body = p
      ? t("{{name}} will get cost price {{cost}} (now {{oldCost}}) and sale price {{sale}} (now {{oldSale}}).", {
          name: p.name || p.productUniqueId,
          cost: rupees(tt?.costPrice, 0),
          oldCost: p.costPrice ? rupees(p.costPrice, 0) : "—",
          sale: rupees(tt?.salePrice, 0),
          oldSale: p.sellPrice ? rupees(p.sellPrice, 0) : "—",
        })
      : t("No product is linked to this saree, so no product price changes.");
    const following = (data?.variants || []).filter((v: any) => !v.ownCosting && !(draft?.header.keepOwnPrice || []).includes(v.productUniqueId)).length;
    const bodyAll = following ? `${body} ${t("{{n}} variant(s) get the same prices.", { n: following })}` : body;
    if (!(await confirmDialog({ title: t("Approve this costing?"), body: bodyAll, ok: t("Approve") }))) return;
    const v = await act(() => approveCosting(id), "Approved. The owner gets an email.");
    if (v) take(v);
  };

  const reject = async () => {
    const why = await askReason({ title: t("Send back: what needs to change?"), ok: t("Send back") });
    if (!why) return;
    const v = await act(() => rejectCosting(id, why), "Sent back with your note.");
    if (v) take(v);
  };

  const withdraw = async () => {
    const v = await act(() => withdrawCosting(id), "Taken back. You can change it now.");
    if (v) take(v);
  };

  const revise = async () => {
    if (!(await confirmDialog({ title: t("Start a new version?"), body: t("The approved prices stay in use until the new version is approved. The owner gets an email.") }))) return;
    const v = await act(() => reviseCosting(id), "New version started.");
    if (v?.costing?.id) history.push(kp.costing(v.costing.id));
  };

  const refresh = async () => {
    if (dirty && !(await save(true))) return;
    const v = await act(() => refreshCostingPrefill(id), "Karkhana numbers brought up to date. Numbers you typed were kept.");
    if (v) take(v);
  };

  const discard = async () => {
    if (!(await confirmDialog({ title: t("Discard this draft?"), body: t("It stays in the history but leaves the list."), danger: true, ok: t("Discard") }))) return;
    const v = await act(() => discardCosting(id), "Draft discarded.");
    if (v) history.push(kp.costings());
  };

  const removeLine = async (i: number) => {
    const l = draft?.lines[i];
    if (!l) return;
    if (l.label && !(await confirmDialog({ title: t("Remove {{name}}?", { name: l.label }), danger: true, ok: t("Remove") }))) return;
    change((d) => ({ ...d, lines: d.lines.filter((_, j) => j !== i) }));
  };

  const addLine = () => {
    if (!adding || !adding.label.trim()) return;
    const l = { ...newLine(adding.group, adding.kind), label: adding.label.trim() };
    change((d) => {
      // New lines go at the end of their group.
      const last = d.lines.map((x) => x.group).lastIndexOf(adding.group);
      const lines = [...d.lines];
      lines.splice(last < 0 ? lines.length : last + 1, 0, l);
      return { ...d, lines };
    });
    setAdding(null);
  };

  const openAudit = async () => {
    if (audit) return;
    try {
      const res: any = await getCostingAudit(id);
      setAudit(res?.success ? res.data || [] : []);
    } catch {
      setAudit([]);
    }
  };

  let blankMarked = false;
  const refFor = (lid: string, fields: string[]) => {
    if (blankMarked || !showBlanks || !calc) return undefined;
    if (calc.blanks.some((b) => b.lineId === lid && fields.includes(b.field))) {
      blankMarked = true;
      return firstBlank;
    }
    return undefined;
  };

  const renderInputs = (l: Line, i: number) => {
    const lid = lineId(l, i);
    const dis = !canEdit;
    switch (l.kind) {
      case "YARN": {
        const kori = koriGrams(l);
        return (
          <div className="kc-inputs">
            <NumIn label={t("Khari g")} value={l.khariGrams} onChange={(v) => setLine(i, { khariGrams: v })} disabled={dis} blank={isBlank(lid, "khariGrams")} width={84} />
            <span className="kc-op">×</span>
            <NumIn label={t("Kori factor")} value={l.koriFactor} onChange={(v) => setLine(i, { koriFactor: v })} disabled={dis} blank={isBlank(lid, "koriFactor")} width={72} />
            <span className="kc-op">=</span>
            <span className="kc-derived">
              <small>{t("Kori g")}</small>
              {kori === null ? "—" : kori}
            </span>
            <span className="kc-op">×</span>
            <NumIn label={t("₹ per kg")} value={l.ratePerKg} onChange={(v) => setLine(i, { ratePerKg: v, materialRateId: null })} disabled={dis} blank={isBlank(lid, "ratePerKg")} width={96} />
            {canEdit && rates.length ? (
              <select
                className="kc-select"
                aria-label={t("Take rate from the list")}
                value={l.materialRateId || ""}
                onChange={(e) => {
                  const r = rates.find((x) => x.id === e.target.value);
                  setLine(i, r ? { materialRateId: r.id, ratePerKg: r.rate ?? l.ratePerKg } : { materialRateId: null });
                }}
              >
                <option value="">{t("Rate list…")}</option>
                {rates.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} {r.rate ? `₹${r.rate}` : t("(no rate)")}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        );
      }
      case "QTY_RATE":
        return (
          <div className="kc-inputs">
            <NumIn label={t("Quantity")} value={l.qty} onChange={(v) => setLine(i, { qty: v })} disabled={dis} blank={isBlank(lid, "qty")} width={84} />
            <label className="kc-num" style={{ width: 70 }}>
              <span className="kc-num__label">{t("Unit")}</span>
              <span className="kc-num__box">
                <input value={l.unit || ""} disabled={dis} placeholder="pcs" onChange={(e) => setLine(i, { unit: e.target.value })} />
              </span>
            </label>
            <span className="kc-op">×</span>
            <NumIn label={t("Rate ₹")} value={l.rate} onChange={(v) => setLine(i, { rate: v })} disabled={dis} blank={isBlank(lid, "rate")} width={96} />
          </div>
        );
      case "SHARED":
        return (
          <div className="kc-inputs">
            <NumIn label={t("Total ₹")} value={l.sharedTotal} onChange={(v) => setLine(i, { sharedTotal: v })} disabled={dis} blank={isBlank(lid, "sharedTotal")} width={104} />
            <span className="kc-op">÷</span>
            <NumIn label={t("Sarees")} value={l.sharedSarees} onChange={(v) => setLine(i, { sharedSarees: v })} disabled={dis} blank={isBlank(lid, "sharedSarees")} width={72} />
          </div>
        );
      case "PERCENT_OF_MATERIAL":
        return (
          <div className="kc-inputs">
            <NumIn label={t("% of material")} value={l.percent} onChange={(v) => setLine(i, { percent: v })} disabled={dis} blank={isBlank(lid, "percent")} suffix="%" width={96} />
            <span className="kc-hint">{t("of {{amt}}", { amt: rupees(calc?.totals.material ?? 0, 0) })}</span>
          </div>
        );
      case "LABOUR": {
        const p = planned(l);
        const d = byDays(l);
        const pick = (m: Method) => canEdit && setLine(i, { method: m });
        const on = (m: Method) => (l.method === m ? " is-on" : "");
        return (
          <div className={`kc-labour${isBlank(lid, "method") ? " is-blank" : ""}`} role="radiogroup" aria-label={t("Which figure counts")}>
            <button type="button" className={`kc-way${on("ACTUAL")}`} onClick={() => pick("ACTUAL")} disabled={!canEdit && l.method !== "ACTUAL"} role="radio" aria-checked={l.method === "ACTUAL"}>
              <small>{t("Paid in Karkhana")}</small>
              <strong>{l.actualAmount === null || l.actualAmount === undefined ? t("no work yet") : rupees(l.actualAmount, 0)}</strong>
            </button>
            <div className={`kc-way${on("PLANNED")}`} onClick={() => pick("PLANNED")} role="radio" aria-checked={l.method === "PLANNED"}>
              <small>{t("Planned")}</small>
              <span className="kc-way__inputs">
                <NumIn label={t("inches")} value={l.plannedInches} onChange={(v) => setLine(i, { plannedInches: v })} disabled={dis} blank={l.method === "PLANNED" && isBlank(lid, "plannedInches")} width={64} />
                <span className="kc-op">×</span>
                <NumIn label={t("₹/in")} value={l.ratePerInch} onChange={(v) => setLine(i, { ratePerInch: v })} disabled={dis} blank={l.method === "PLANNED" && isBlank(lid, "ratePerInch")} width={76} />
              </span>
              <strong>{p === null ? "—" : rupees(p, 0)}</strong>
            </div>
            <div className={`kc-way${on("DAYS")}`} onClick={() => pick("DAYS")} role="radio" aria-checked={l.method === "DAYS"}>
              <small>{t("Days × day rate")}</small>
              <span className="kc-way__inputs">
                <NumIn label={t("days")} value={l.days} onChange={(v) => setLine(i, { days: v })} disabled={dis} blank={l.method === "DAYS" && isBlank(lid, "days")} width={56} />
                <span className="kc-op">×</span>
                <NumIn label={t("₹/day")} value={l.dayRate} onChange={(v) => setLine(i, { dayRate: v })} disabled={dis} blank={l.method === "DAYS" && isBlank(lid, "dayRate")} width={64} />
              </span>
              <strong>{d === null ? "—" : rupees(d, 0)}</strong>
            </div>
            <div className={`kc-way${on("MANUAL")}`} onClick={() => pick("MANUAL")} role="radio" aria-checked={l.method === "MANUAL"}>
              <small>{t("Type amount")}</small>
              <NumIn label={t("₹")} value={l.method === "MANUAL" ? l.amount : ""} onChange={(v) => setLine(i, { amount: v, method: "MANUAL" })} disabled={dis} blank={l.method === "MANUAL" && isBlank(lid, "amount")} width={84} />
            </div>
          </div>
        );
      }
      default:
        return (
          <div className="kc-inputs">
            <NumIn label={t("Amount ₹")} value={l.amount} onChange={(v) => setLine(i, { amount: v })} disabled={dis} blank={isBlank(lid, "amount")} width={110} />
          </div>
        );
    }
  };

  const status = c?.status as string | undefined;
  const pageName = draft?.header.sareeNumber ? `${t("Saree")} ${draft.header.sareeNumber}` : `${t("Estimate")} ${draft?.header.designNumber || ""}`;
  const rememberHere = () => {
    const ids = [data?.product?.productUniqueId, ...((data?.variants || []) as any[]).map((v) => v.productUniqueId)].filter(Boolean);
    // The product page's Back returns to this exact costing address; every variant of the family counts.
    rememberKarkhanaHere(history.location, null, `${t("Costing")} · ${pageName}`, ids);
  };
  const download = () => setPreview(true);
  const toggleGroup = (g: string) =>
    setOpenGroups((s) => {
      const n = new Set(s);
      n.has(g) ? n.delete(g) : n.add(g);
      return n;
    });
  const tt = calc?.totals;
  const h = draft?.header || {};

  return (
    <div className="gi-registry-page kw-page kc-page">
      <PageHeader
        title={
          <>
            {t("Costing")}{draft ? ` · ${pageName}` : ""}
            {status ? <span className={`kc-status is-${status.toLowerCase()}`}>{t(STATUS_LABEL[status] || status)} · v{c.versionNo}</span> : null}
          </>
        }
        back={kp.costings()}
        crumbs={draft ? [{ to: kp.costings(), label: t("Costing") }] : undefined}
        trail={draft ? pageName : undefined}
      >
        {data ? (
          <HeaderButton onClick={download} title={t("See the PDF and download it for the file")}>
            {t("Download")}
          </HeaderButton>
        ) : null}
        {canEdit ? (
          <>
            <HeaderButton onClick={() => save()} disabled={busy || !dirty} primary={dirty}>
              {t("Save")}
            </HeaderButton>
            <HeaderButton onClick={submit} disabled={busy} primary={!dirty}>
              {t("Send for approval")}
            </HeaderButton>
          </>
        ) : null}
        {status === "SUBMITTED" && data?.canApprove ? (
          <>
            <HeaderButton onClick={reject} disabled={busy}>
              {t("Send back")}
            </HeaderButton>
            <HeaderButton onClick={approve} disabled={busy} primary>
              {t("Approve")}
            </HeaderButton>
          </>
        ) : null}
        {status === "SUBMITTED" && !data?.canApprove ? (
          <HeaderButton onClick={withdraw} disabled={busy}>
            {t("Take back to edit")}
          </HeaderButton>
        ) : null}
        {data?.canRevise ? (
          <HeaderButton onClick={revise} disabled={busy}>
            {t("Change (new version)")}
          </HeaderButton>
        ) : null}
      </PageHeader>

      {!data || !draft ? <div className="kw-empty">{t("Loading…")}</div> : null}

      {data && draft && tt ? (
        <>
          {status === "REJECTED" && c.decisionNote ? (
            <p className="kc-note is-warn">
              <strong>{t("Sent back by {{name}}:", { name: c.decidedByName })}</strong> {c.decisionNote}
            </p>
          ) : null}
          {status === "SUBMITTED" ? (
            <p className="kc-note">{t("Waiting for the owner's approval since {{when}}. It can't be changed now.", { when: fmtDay(localDay(c.submittedAt)) })}</p>
          ) : null}
          {status === "APPROVED" ? (
            <p className="kc-note is-ok">
              {t("Approved by {{name}} on {{when}}.", { name: c.decidedByName, when: fmtDay(localDay(c.decidedAt)) })}{" "}
              {c.pricesPushed ? t("The product's prices were updated.") : ""}
              {c.variantsPriced ? ` ${t("{{n}} variant(s) got the same prices.", { n: c.variantsPriced })}` : ""}
            </p>
          ) : null}

          <section className="kc-card kc-facts">
            <div>
              <span>{t("Saree")}</span>
              <strong>
                {h.pieceId ? <Link to={kp.saree(h.pieceId)}>{h.sareeNumber}</Link> : t("Estimate (no saree yet)")}
              </strong>
            </div>
            <div>
              <span>{t("Design")}</span>
              <strong>
                {h.designNumber || "—"}
                {h.designName ? <small> {h.designName}</small> : null}
              </strong>
            </div>
            <div>
              <span>{t("Loom")}</span>
              <strong>{h.loomCode || "—"}</strong>
            </div>
            {!data.product ? (
              <div>
                <span>{t("Product")}</span>
                <strong>
                  <small>{h.pieceId ? t("Not linked — link it from the saree page") : t("Not linked")}</small>
                </strong>
              </div>
            ) : null}
            <NumIn label={t("Saree weight (g)")} value={h.weightGrams} onChange={(v) => setHeader({ weightGrams: v })} disabled={!canEdit} blank={isBlank("header", "weightGrams")} width={130} />
            <NumIn label={t("Length (in)")} value={h.lengthInches} onChange={(v) => setHeader({ lengthInches: v })} disabled={!canEdit} width={110} />
            <label className="kc-num kc-wide">
              <span className="kc-num__label">{t("Weavers")}</span>
              <span className="kc-num__box">
                <input value={h.weavers || ""} disabled={!canEdit} onChange={(e) => setHeader({ weavers: e.target.value })} />
              </span>
            </label>
            <details className="kc-specs">
              <summary>{t("Loom details (optional)")}</summary>
              <div className="kc-inputs">
                {(
                  [
                    ["loomType", "Loom type"],
                    ["reed", "Reed"],
                    ["warpDenier", "Warp denier"],
                    ["weftDenier", "Weft denier"],
                    ["epi", "EPI"],
                    ["ppi", "PPI"],
                  ] as [keyof Header, string][]
                ).map(([k, label]) => (
                  <label key={k} className="kc-num" style={{ width: 120 }}>
                    <span className="kc-num__label">{t(label)}</span>
                    <span className="kc-num__box">
                      <input value={(h[k] as string) || ""} disabled={!canEdit} onChange={(e) => setHeader({ [k]: e.target.value } as Partial<Header>)} />
                    </span>
                  </label>
                ))}
              </div>
            </details>
          </section>

          <CostingProductPanel
            onOpenProduct={rememberHere}
            onStartCosting={async (pieceId) => {
              const v = await act(() => createCosting({ pieceId }), "Costing started for that saree.");
              if (v?.costing?.id) history.push(kp.costing(v.costing.id));
            }}
            product={data.product}
            variants={data.variants}
            keepOwn={h.keepOwnPrice || []}
            canEdit={canEdit}
            onKeepOwn={(ids) => setHeader({ keepOwnPrice: ids })}
          />

          {h.pieceId ? (
            <div className="kc-karkhana">
              <span>
                {data.karkhana?.hasWork
                  ? t("Karkhana has paid {{amt}} for this saree so far ({{in}} in woven).", { amt: rupees(data.karkhana.paid, 0), in: data.karkhana.sareeInches })
                  : t("No Karkhana work is recorded for this saree yet.")}
              </span>
              {canEdit ? (
                <button type="button" className="kh-btn kh-btn--sm" onClick={refresh} disabled={busy}>
                  {t("Update from Karkhana")}
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="kc-groups-bar">
            <button type="button" className="kc-addlink" onClick={() => setOpenGroups(openGroups.size ? new Set() : new Set(GROUPS.map((g) => g.id)))}>
              {openGroups.size ? t("Fold all") : t("Open all lines")}
            </button>
          </div>
          {GROUPS.map((g) => {
            const rows = draft.lines.map((l, i) => ({ l, i })).filter((x) => x.l.group === g.id);
            if (!rows.length && !canEdit) return null;
            const groupTotal = rows.reduce((s, x) => s + (calc?.amounts[lineId(x.l, x.i)] || 0), 0);
            const empties = calc ? new Set(calc.blanks.filter((b) => rows.some((x) => lineId(x.l, x.i) === b.lineId)).map((b) => b.lineId)).size : 0;
            const open = openGroups.has(g.id) || (showBlanks && empties > 0);
            return (
              <section key={g.id} className={`kc-group${open ? " is-open" : ""}`}>
                <button type="button" className="kc-group__toggle" aria-expanded={open} onClick={() => toggleGroup(g.id)}>
                  <span className="kc-chev" aria-hidden="true">›</span>
                  <span className="kc-group__name">
                    {t(g.title)} <small>{g.hi}</small>
                  </span>
                  <small className="kc-group__meta">
                    {t("{{n}} lines", { n: rows.length })}
                    {empties ? <em> · {t("{{n}} empty", { n: empties })}</em> : null}
                  </small>
                  <strong>{rupees(groupTotal, 0)}</strong>
                </button>
                {open ? (
                <>
                <div className="kc-table">
                  {rows.map(({ l, i }) => {
                    const lid = lineId(l, i);
                    const e = l.id ? est[l.id] : null;
                    const amt = calc?.amounts[lid];
                    const fields = ["khariGrams", "koriFactor", "ratePerKg", "qty", "rate", "sharedTotal", "sharedSarees", "percent", "method", "actualAmount", "plannedInches", "ratePerInch", "days", "dayRate", "amount"];
                    return (
                      <div key={lid} className={`kc-row${l.kind === "LABOUR" ? " is-labour" : ""}`} ref={refFor(lid, fields)}>
                        <div className="kc-row__name">
                          {canEdit ? (
                            <input className="kc-label" value={l.label} aria-label={t("Line name")} onChange={(ev) => setLine(i, { label: ev.target.value })} />
                          ) : (
                            <strong>{l.label}</strong>
                          )}
                          {l.labelHi ? <small className="kc-hi">{l.labelHi}</small> : null}
                          {l.sourceNote ? <small className={`kc-source${l.source ? "" : " is-typed"}`}>{l.source ? l.sourceNote : t("Changed by hand · {{note}}", { note: l.sourceNote })}</small> : null}
                        </div>
                        <div className="kc-row__inputs">{renderInputs(l, i)}</div>
                        <div className={`kc-row__amt${amt === 0 ? " is-zero" : ""}`}>{amt === null || amt === undefined ? <span className="kc-empty">{t("empty")}</span> : rupees(amt, 0)}</div>
                        <div className={`kc-row__est${e?.level === "WARN" ? " is-warn" : ""}`}>
                          {e ? (
                            <>
                              {e.reference ? (
                                <span>
                                  {t("HKP estimate")} <strong>{rupees(e.reference, 0)}</strong>
                                </span>
                              ) : null}
                              {(e.notes || []).map((n: string) => (
                                <small key={n}>{e.level === "WARN" ? "⚠ " : ""}{t(n)}</small>
                              ))}
                            </>
                          ) : (
                            <small className="kc-muted">{t("No estimate yet")}</small>
                          )}
                        </div>
                        <div className="kc-row__act">
                          {canEdit ? (
                            <button type="button" className="kc-x" aria-label={t("Remove {{name}}", { name: l.label })} onClick={() => removeLine(i)}>
                              ×
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {canEdit ? (
                  adding?.group === g.id ? (
                    <div className="kc-add">
                      <input autoFocus placeholder={t("Line name, e.g. Meenakari thread")} value={adding.label} onChange={(e) => setAdding({ ...adding, label: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addLine()} />
                      <select className="kc-select" value={adding.kind} onChange={(e) => setAdding({ ...adding, kind: e.target.value as Kind })}>
                        {KINDS.map((k) => (
                          <option key={k.id} value={k.id}>
                            {t(k.title)}
                          </option>
                        ))}
                      </select>
                      <button type="button" className="kh-btn kh-btn--sm is-primary" onClick={addLine} disabled={!adding.label.trim()}>
                        {t("Add")}
                      </button>
                      <button type="button" className="kh-btn kh-btn--sm" onClick={() => setAdding(null)}>
                        {t("Cancel")}
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="kc-addlink" onClick={() => setAdding({ group: g.id, kind: g.id === "MATERIAL" ? "YARN" : "LUMP", label: "" })}>
                      + {t("Add a line")}
                    </button>
                  )
                ) : null}
                </>
                ) : null}
              </section>
            );
          })}

          <section className="kc-card kc-layers">
            <h3 className="kc-group__title">
              <span>
                {t("From cost to sale price")} <small>लागत से बिक्री मूल्य</small>
              </span>
            </h3>
            <div className="kc-ladder">
              <div className="kc-step">
                <span>{t("Production cost")}</span>
                <strong>{rupees(tt.production, 0)}</strong>
              </div>
              <div className="kc-step">
                <NumIn label={t("Business overhead")} value={draft.layers.overheadPct} onChange={(v) => setLayer({ overheadPct: v })} disabled={!canEdit} blank={isBlank("layers", "overheadPct")} suffix="%" width={110} />
                <strong>+ {rupees(tt.overhead, 0)}</strong>
              </div>
              <div className="kc-step is-key">
                <span>{t("Cost price")}</span>
                <strong>{rupees(tt.costPrice, 0)}</strong>
              </div>
              <div className="kc-step">
                <NumIn label={t("Profit")} value={draft.layers.profitPct} onChange={(v) => setLayer({ profitPct: v })} disabled={!canEdit} blank={isBlank("layers", "profitPct")} suffix="%" width={110} />
                <label className="kc-num" style={{ width: 120 }}>
                  <span className="kc-num__label">{t("Round up to")}</span>
                  <span className="kc-num__box">
                    <select value={draft.layers.roundTo ?? ""} disabled={!canEdit} onChange={(e) => setLayer({ roundTo: e.target.value })}>
                      <option value="">—</option>
                      <option value="0">{t("No rounding")}</option>
                      <option value="10">₹10</option>
                      <option value="50">₹50</option>
                      <option value="100">₹100</option>
                      <option value="500">₹500</option>
                    </select>
                  </span>
                </label>
                <strong>+ {rupees(tt.profit, 0)}</strong>
              </div>
              <div className="kc-step is-key">
                <span>{t("Sale price")}</span>
                <strong>{rupees(tt.salePrice, 0)}</strong>
              </div>
              <div className="kc-step">
                <NumIn label={t("Discount")} value={draft.layers.discountPct} onChange={(v) => setLayer({ discountPct: v })} disabled={!canEdit} blank={isBlank("layers", "discountPct")} suffix="%" width={110} />
                <strong>− {rupees(tt.discount, 0)}</strong>
              </div>
              <div className="kc-step">
                <NumIn label={t("GST")} value={draft.layers.gstPct} onChange={(v) => setLayer({ gstPct: v })} disabled={!canEdit} blank={isBlank("layers", "gstPct")} suffix="%" width={110} />
                <strong>+ {rupees(tt.gst, 0)}</strong>
              </div>
              <div className="kc-step is-key">
                <span>{t("Price with GST")}</span>
                <strong>{rupees(tt.finalPrice, 0)}</strong>
              </div>
            </div>
            {warnings.length ? (
              <ul className="kc-warnings">
                {warnings.map((w: any, k: number) => (
                  <li key={k}>⚠ {t(w.message)}</li>
                ))}
              </ul>
            ) : null}
            {dirty ? <p className="kc-muted">{t("HKP's estimates and warnings update when you save.")}</p> : null}
            <label className="kc-notefield">
              <span>{t("Note for the approver")}</span>
              <textarea value={draft.note || ""} disabled={!canEdit} rows={2} onChange={(e) => change((d) => ({ ...d, note: e.target.value }))} />
            </label>
          </section>

          <section className="kc-card">
            <details onToggle={(e) => (e.currentTarget as HTMLDetailsElement).open && openAudit()}>
              <summary>{t("History of changes")}</summary>
              {audit === null ? <p className="kc-muted">{t("Loading…")}</p> : null}
              {audit && !audit.length ? <p className="kc-muted">{t("Nothing yet.")}</p> : null}
              <ol className="kc-audit">
                {(audit || []).map((a: any) => (
                  <li key={a.id}>
                    <div>
                      <strong>{t(a.action.replace(/_/g, " ").toLowerCase().replace(/^./, (x: string) => x.toUpperCase()))}</strong> · v{a.versionNo} · {a.byName} ·{" "}
                      {new Date(a.at).toLocaleString(lang === "hi" ? "hi-IN" : "en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                    {a.note ? <small>{a.note}</small> : null}
                    {(a.changes || []).length ? (
                      <ul>
                        {a.changes.slice(0, 40).map((ch: any, k: number) => (
                          <li key={k}>
                            {ch.label}
                            {ch.field && ch.field !== "line" && ch.field !== ch.label ? ` · ${FIELD_LABEL[ch.field] || ch.field}` : ""}: {ch.from ?? "—"} → {ch.to ?? "—"}
                          </li>
                        ))}
                        {a.changes.length > 40 ? <li>{t("and {{n}} more", { n: a.changes.length - 40 })}</li> : null}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ol>
            </details>
            {(data.versions || []).length > 1 ? (
              <div className="kc-versions">
                <span>{t("Versions")}:</span>
                {data.versions.map((v: any) => (
                  <Link key={v.id} to={kp.costing(v.id)} className={v.id === c.id ? "is-current" : ""}>
                    v{v.versionNo} {t(STATUS_LABEL[v.status] || v.status)} {v.salePrice ? rupees(v.salePrice, 0) : ""}
                  </Link>
                ))}
              </div>
            ) : null}
            {canEdit ? (
              <button type="button" className="kc-discard" onClick={discard} disabled={busy}>
                {t("Discard this draft")}
              </button>
            ) : null}
          </section>

          <CostingPdfPreview open={preview} onClose={() => setPreview(false)} title={`Costing ${pageName} v${c?.versionNo ?? ""}`}>
            <CostingPrint costing={c} lines={draft.lines} layers={draft.layers} header={h} calc={calc} product={data.product} variants={data.variants} />
          </CostingPdfPreview>

          <div className="kc-totalbar" role="status">
            <div>
              <span>{t("Production")}</span>
              <strong>{rupees(tt.production, 0)}</strong>
            </div>
            <div>
              <span>{t("Cost price")}</span>
              <strong>{rupees(tt.costPrice, 0)}</strong>
            </div>
            <div className="is-key">
              <span>{t("Sale price")}</span>
              <strong>{rupees(tt.salePrice, 0)}</strong>
            </div>
            <div>
              <span>{t("Margin")}</span>
              <strong>{tt.marginPct === null ? "—" : `${tt.marginPct}%`}</strong>
            </div>
            <div className={`kc-total-empty${calc && calc.blanks.length ? " is-warn" : ""}`}>
              <span>{t("Empty")}</span>
              <strong>{calc ? calc.blanks.length + calc.bad.length : 0}</strong>
            </div>
            {dirty ? <em>{t("Unsaved")}</em> : null}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default KarkhanaCostingPage;
